import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCrypto } from '../contexts/CryptoContext';
import { useNavigate } from 'react-router-dom';
import { checkinService } from '../services/checkinService';
import { getAll as getBeneficiaries } from '../services/beneficiaryService';
import { GRACE_OPTIONS, GRACE_DEFAULT } from '../config/checkinConfig';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

/* ── GlowCard ─────────────────────────────────────────────────────────── */
const GlowCard = ({ children, style = {}, compact = false }) => (
  <div style={{
    position: 'relative', borderRadius: '16px', padding: '2px',
    overflow: 'hidden', background: 'rgba(0,245,160,0.15)', ...style,
  }}>
    <div style={{
      position: 'absolute', top: '-50%', left: '-50%',
      width: '200%', height: '200%',
      background: 'conic-gradient(from 0deg, transparent 0%, transparent 30%, rgba(0,245,160,0.6) 40%, rgba(0,245,160,1) 50%, rgba(0,245,160,0.6) 60%, transparent 70%, transparent 100%)',
      animation: 'glowSpin 6s linear infinite',
    }} />
    <div style={{ position: 'relative', zIndex: 1, background: '#0e1a2e', borderRadius: '14px', padding: compact ? '1rem' : '1.5rem' }}>
      {children}
    </div>
  </div>
);

const iconWrap = {
  width: '40px', height: '40px', borderRadius: '10px',
  background: 'rgba(0,245,160,0.1)',
  boxShadow: '0 0 16px rgba(0,245,160,0.12), inset 0 0 16px rgba(0,245,160,0.04)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem',
};

const S = '#00F5A0';
const G = '#8899AA';

const ShieldIcon   = ({ size = 22, color = S }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const ClockIcon    = ({ size = 22, color = S }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const CalendarIcon = ({ size = 22, color = S }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const CheckIcon    = ({ size = 20, color = '#0A1628' }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>;
const AlertIcon    = ({ size = 16, color = '#FF4757' }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;

const FREQUENCIES = [
  { value: 'weekly',    label: 'Hebdomadaire' },
  { value: 'monthly',   label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'biannual',  label: 'Semestriel' },
  { value: 'vacation',  label: 'Congés exceptionnels' },
  { value: 'demo',      label: 'Démo 2 min' },
];

const visibleFrequencies = IS_DEMO ? FREQUENCIES : FREQUENCIES.filter(f => f.value !== 'demo');
const visibleGrace       = IS_DEMO ? GRACE_OPTIONS : GRACE_OPTIONS.filter(g => g.value !== 0.007);

const computeStatus = (enabled, nextDue) => {
  if (!enabled)  return 'inactive';
  if (!nextDue)  return 'pending';
  if (new Date(nextDue) < new Date()) return 'overdue';
  return 'active';
};

const STATUS_DISPLAY = {
  inactive: { label: 'Inactif',     color: '#FF4757' },
  pending:  { label: 'Non démarré', color: '#FECA57' },
  active:   { label: 'Actif',       color: S },
  overdue:  { label: 'En retard',   color: '#FF4757' },
};

const CheckinPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { masterKey } = useCrypto();
  const navigate = useNavigate();

  const [checkinEnabled,     setCheckinEnabled]     = useState(false);
  const [frequency,          setFrequency]          = useState('monthly');
  const [vacationReturnDate, setVacationReturnDate] = useState('');
  const [gracePeriod,        setGracePeriod]        = useState(GRACE_DEFAULT);
  const [message,            setMessage]            = useState('');
  const [confirmed,          setConfirmed]          = useState(false);
  const [savedConfig,        setSavedConfig]        = useState(null);
  const [checkinStatus,      setCheckinStatus]      = useState({ last_checkin: null, next_checkin_due: null });
  const [noBeneficiaries,    setNoBeneficiaries]    = useState(false);
  const [loading,            setLoading]            = useState(false);
  const [saveSuccess,        setSaveSuccess]        = useState(false);
  const [saveMessage,        setSaveMessage]        = useState('');
  const [error,              setError]              = useState('');
  const d = new Date();
  const todayISO = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const MAX_VACATION_MONTHS = 6;
  const dMax = new Date(d); dMax.setMonth(dMax.getMonth() + MAX_VACATION_MONTHS);
  const maxVacationISO = `${dMax.getFullYear()}-${String(dMax.getMonth()+1).padStart(2,'0')}-${String(dMax.getDate()).padStart(2,'0')}`;
  const isDirty = savedConfig !== null && (
    savedConfig.enabled            !== checkinEnabled      ||
    savedConfig.frequency          !== frequency           ||
    savedConfig.gracePeriod        !== gracePeriod         ||
    savedConfig.message            !== message             ||
    savedConfig.vacationReturnDate !== vacationReturnDate
  );
  const vacationExpired = frequency === 'vacation' && !!vacationReturnDate && vacationReturnDate < todayISO;

  const status        = computeStatus(checkinEnabled, checkinStatus.next_checkin_due);
  const statusDisplay = STATUS_DISPLAY[status];
  const nextDueDate   = checkinStatus.next_checkin_due;

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes glowSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      textarea::placeholder, input::placeholder { color: #556677; }
      input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.4); cursor: pointer; }
      select option { background: #0A1628; color: #E8ECF1; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const [configResult, benefResult] = await Promise.all([
        checkinService.getConfig(),
        getBeneficiaries(masterKey),
      ]);

      const benefList = benefResult?.data ?? [];
      setNoBeneficiaries(benefList.length === 0);

      if (!configResult.success || !configResult.data) {
        setSavedConfig({ enabled: false, frequency: 'monthly', gracePeriod: GRACE_DEFAULT, message: '' });
        return;
      }

      const cfg     = configResult.data;
      const enabled = cfg.is_enabled          ?? false;
      const freq    = cfg.frequency           ?? 'monthly';
      const grace   = cfg.grace_period_days   ?? GRACE_DEFAULT;
      const msg     = cfg.custom_message      ?? '';
      const vacDate = cfg.vacation_return_date ? cfg.vacation_return_date.split('T')[0] : '';

      setCheckinEnabled(enabled);
      setFrequency(freq);
      setGracePeriod(grace);
      setMessage(msg);
      setVacationReturnDate(vacDate);
      setSavedConfig({ enabled, frequency: freq, gracePeriod: grace, message: msg, vacationReturnDate: vacDate });

      if (cfg.last_check_in) {
        setCheckinStatus({
          last_checkin:     new Date(cfg.last_check_in),
          next_checkin_due: cfg.next_check_in_due ? new Date(cfg.next_check_in_due) : null,
        });
      }
    } catch (err) {
      console.error('[SafeHeir] Erreur loadConfig:', err);
      setError('Erreur lors du chargement de la configuration.');
    }
  }, [masterKey]);

  useEffect(() => {
    if (!isAuthenticated || !user) { navigate('/login'); return; }
    loadConfig();
  }, [isAuthenticated, user, navigate, loadConfig]);

  const calculateNextCheckin = () => {
    const next = new Date();
    if (frequency === 'vacation' && vacationReturnDate) return new Date(vacationReturnDate);
    switch (frequency) {
      case 'weekly':    next.setDate(next.getDate() + 7);       break;
      case 'monthly':   next.setMonth(next.getMonth() + 1);     break;
      case 'quarterly': next.setMonth(next.getMonth() + 3);     break;
      case 'biannual':  next.setMonth(next.getMonth() + 6);     break;
      case 'demo':      next.setMinutes(next.getMinutes() + 2); break;
      default:          next.setMonth(next.getMonth() + 1);
    }
    return next;
  };

  const handleSaveSettings = async () => {
    if (frequency === 'vacation') {
      if (!vacationReturnDate) { setError('Veuillez saisir votre date de retour.'); return; }
      if (vacationReturnDate < todayISO) { setError("La date de retour doit être aujourd'hui ou dans le futur."); return; }
      if (vacationReturnDate > maxVacationISO) { setError(`La date de retour ne peut pas dépasser ${MAX_VACATION_MONTHS} mois.`); return; }
    }
    try {
      setLoading(true); setError('');
      const frequencyChanged = savedConfig?.frequency !== frequency;
      const shouldRecompute  = frequencyChanged || frequency === 'vacation';
      const payload = {
        is_enabled:           checkinEnabled,
        frequency,
        grace_period_days:    gracePeriod,
        custom_message:       message,
        vacation_return_date: frequency === 'vacation' ? new Date(vacationReturnDate).toISOString() : null,
      };
      if (shouldRecompute) payload.next_check_in_due = calculateNextCheckin().toISOString();
      const result = await checkinService.saveConfig(payload);
      if (!result.success) { setError(result.error || 'Erreur lors de la sauvegarde'); return; }
      setSavedConfig({ enabled: checkinEnabled, frequency, gracePeriod, message, vacationReturnDate });
      if (payload.next_check_in_due) {
        setCheckinStatus(prev => ({ ...prev, next_checkin_due: new Date(payload.next_check_in_due) }));
      }
      setSaveMessage('Paramètres sauvegardés avec succès');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch { setError('Erreur lors de la sauvegarde'); }
    finally { setLoading(false); }
  };

  const handleDisable = async () => {
    try {
      setLoading(true); setError('');
      const result = await checkinService.saveConfig({
        is_enabled: false, frequency, grace_period_days: gracePeriod, custom_message: message,
      });
      if (!result.success) { setError(result.error || 'Erreur lors de la désactivation'); return; }
      setCheckinEnabled(false);
      setSavedConfig(prev => ({ ...(prev ?? { frequency, gracePeriod, message }), enabled: false }));
      setSaveMessage('Protection automatique désactivée');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch { setError('Erreur lors de la désactivation'); }
    finally { setLoading(false); }
  };

  const performCheckin = async () => {
    try {
      const next   = calculateNextCheckin();
      const result = await checkinService.performCheckin(30, next);
      if (!result.success) { setError(result.error || 'Erreur lors du check-in'); return; }
      setCheckinStatus({ last_checkin: new Date(), next_checkin_due: next });
      setConfirmed(true);
      setTimeout(() => setConfirmed(false), 5000);
    } catch { setError('Erreur lors du check-in'); }
  };

  const fieldStyle = {
    width: '100%', padding: '12px 14px', boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,245,160,0.15)',
    borderRadius: '10px', color: '#E8ECF1', fontSize: '14px', outline: 'none',
  };
  const selBtn = (active) => ({
    padding: '10px 16px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
    border: active ? `1px solid ${S}` : '1px solid rgba(255,255,255,0.1)',
    background: active ? 'rgba(0,245,160,0.06)' : 'rgba(255,255,255,0.04)',
    color: active ? S : G, transition: 'all 0.2s',
  });
  const sectionLabel = { fontSize: '14px', fontWeight: '500', color: '#E8ECF1', marginBottom: '0.75rem', display: 'block' };

  if (!user) return (
    <div style={{ minHeight: '100vh', background: '#0A1628', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: S }} />
    </div>
  );

  return (
    <div className="min-h-screen pt-24 pb-12 px-6 md:px-12" style={{ background: '#0A1628' }}>
      <div style={{ maxWidth: '1024px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#fff', marginBottom: '0.35rem' }}>
            Protection automatique
          </h1>
          <p style={{ fontSize: '15px', color: G }}>
            Un simple check-in régulier confirme votre présence. Sans réponse, vos proches sont alertés selon vos instructions.
          </p>
        </div>

        {/* Avertissement : protection active sans bénéficiaire */}
        {checkinEnabled && noBeneficiaries && (
          <div style={{
            background: 'rgba(254,202,87,0.07)', border: '1px solid rgba(254,202,87,0.3)',
            borderRadius: '12px', padding: '12px 16px', marginBottom: '1.5rem',
            display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
          }}>
            <AlertIcon size={16} color="#FECA57" />
            <span style={{ fontSize: '14px', color: '#FECA57', fontWeight: '500' }}>
              Protection active sans bénéficiaire —{' '}
            </span>
            <span style={{ fontSize: '14px', color: '#B8960A' }}>personne ne sera alerté si vous manquez un check-in.</span>
            <span onClick={() => navigate('/beneficiaries')} style={{
              fontSize: '14px', color: '#FECA57', cursor: 'pointer',
              textDecoration: 'underline', textDecorationColor: 'rgba(254,202,87,0.4)',
            }}>Ajouter un bénéficiaire →</span>
          </div>
        )}

        {saveSuccess && (
          <div style={{
            background: 'rgba(0,245,160,0.1)', border: `1px solid rgba(0,245,160,0.3)`,
            borderRadius: '10px', padding: '12px 16px', color: S, fontSize: '14px',
            marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <CheckIcon size={16} color={S} /> {saveMessage}
          </div>
        )}
        {error && (
          <div style={{
            background: 'rgba(255,71,87,0.1)', border: '1px solid rgba(255,71,87,0.25)',
            borderRadius: '10px', padding: '12px 16px', color: '#FF4757',
            fontSize: '14px', marginBottom: '1.5rem',
          }}>{error}</div>
        )}

        {/* Guide pédagogique — visible uniquement si protection inactive */}
        {!checkinEnabled && (
          <div style={{
            background: '#0D1E35',
            border: '1px solid rgba(0,245,160,0.12)',
            borderRadius: '14px',
            padding: '20px 24px',
            marginBottom: '1.5rem',
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#E8ECF1', marginBottom: '16px' }}>
              Comment configurer votre check-in ?
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                'Activez la protection (toggle)',
                'Choisissez votre fréquence',
                'Définissez votre période de grâce',
                'Cliquez sur "Paramètres à jour"',
              ].map((text, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(0,245,160,0.12)', border: '1px solid rgba(0,245,160,0.25)',
                    color: S, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: '700',
                  }}>{i + 1}</div>
                  <span style={{ fontSize: '13px', color: '#8899AA' }}>
                    <span style={{ color: '#C8D4E0' }}>Étape {i + 1} : </span>{text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <GlowCard compact>
            <div style={{ textAlign: 'center' }}>
              <div style={iconWrap}><ShieldIcon size={18} color={statusDisplay.color} /></div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: statusDisplay.color, marginBottom: '0.25rem' }}>{statusDisplay.label}</div>
              <div style={{ fontSize: '13px', color: G }}>Statut</div>
            </div>
          </GlowCard>
          <GlowCard compact>
            <div style={{ textAlign: 'center' }}>
              <div style={iconWrap}><ClockIcon size={18} /></div>
              <div style={{ fontSize: checkinStatus.last_checkin ? '18px' : '20px', fontWeight: '700', color: '#fff', marginBottom: '0.25rem' }}>
                {checkinStatus.last_checkin ? checkinStatus.last_checkin.toLocaleDateString('fr-FR') : 'Jamais'}
              </div>
              <div style={{ fontSize: '13px', color: G }}>Dernier check-in</div>
            </div>
          </GlowCard>
          <GlowCard compact>
            <div style={{ textAlign: 'center' }}>
              <div style={iconWrap}><CalendarIcon size={18} color={status === 'overdue' ? '#FF4757' : S} /></div>
              <div style={{
                fontSize: (status === 'pending' || !nextDueDate) ? '13px' : '18px',
                fontWeight: '700', color: status === 'overdue' ? '#FF4757' : '#fff',
                marginBottom: '0.25rem', lineHeight: '1.4',
              }}>
                {status === 'pending'
                  ? 'Effectuez votre premier check-in ↓'
                  : nextDueDate ? new Date(nextDueDate).toLocaleDateString('fr-FR') : 'Non programmé'}
              </div>
              <div style={{ fontSize: '13px', color: G }}>Prochain check-in</div>
            </div>
          </GlowCard>
        </div>

        {/* Configuration */}
        <div style={{ marginBottom: '1.5rem' }}>
          <GlowCard>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#fff', margin: 0 }}>Configuration du check-in</h2>
              {isDirty && (
                <span style={{
                  fontSize: '12px', color: '#FECA57',
                  background: 'rgba(254,202,87,0.1)', border: '1px solid rgba(254,202,87,0.25)',
                  borderRadius: '6px', padding: '3px 10px', fontWeight: '500',
                }}>Modifications non sauvegardées</span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>

              {/* Toggle */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div onClick={() => setCheckinEnabled(v => !v)} style={{
                  width: '48px', height: '26px', borderRadius: '13px', position: 'relative',
                  cursor: 'pointer', flexShrink: 0,
                  background: checkinEnabled ? S : 'rgba(255,255,255,0.1)', transition: 'background 0.3s',
                }}>
                  <div style={{
                    position: 'absolute', top: '3px', left: checkinEnabled ? '25px' : '3px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    background: '#fff', transition: 'left 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }} />
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: '#E8ECF1', marginBottom: '4px' }}>
                    Activer ma protection automatique
                  </div>
                  <div style={{ fontSize: '13px', color: G }}>
                    Vos bénéficiaires seront alertés et recevront vos consignes si votre check-in n'est pas effectué
                  </div>
                </div>
              </div>

              {/* Fréquence */}
              <div>
                <span style={sectionLabel}>Fréquence de vérification</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                  {visibleFrequencies.map(({ value, label }) => (
                    <button key={value} onClick={() => { setFrequency(value); setError(''); }} style={{ ...selBtn(frequency === value), position: 'relative' }}>
                      {label}
                      {value === 'demo' && (
                        <span style={{
                          position: 'absolute', top: '-7px', right: '-7px',
                          background: 'rgba(254,202,87,0.2)', color: '#FECA57',
                          fontSize: '9px', fontWeight: '700', letterSpacing: '0.05em',
                          padding: '2px 5px', borderRadius: '4px', lineHeight: 1.4,
                        }}>TEST</span>
                      )}
                    </button>
                  ))}
                </div>
                {frequency === 'vacation' && (
                  <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(0,245,160,0.04)', border: '1px solid rgba(0,245,160,0.1)', borderRadius: '10px' }}>
                    <label style={{ display: 'block', fontSize: '13px', color: G, marginBottom: '8px' }}>Date de retour prévue</label>
                    <input type="date" value={vacationReturnDate} min={todayISO} max={maxVacationISO}
                      onChange={e => { setVacationReturnDate(e.target.value); setError(''); }}
                      style={{ ...fieldStyle, width: 'auto', minWidth: '200px' }} />
                    {vacationExpired ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', padding: '8px 12px', background: 'rgba(254,202,87,0.08)', border: '1px solid rgba(254,202,87,0.25)', borderRadius: '8px' }}>
                        <AlertIcon size={14} color="#FECA57" />
                        <span style={{ fontSize: '12px', color: '#FECA57', lineHeight: '1.5' }}>
                          Congés terminés — choisissez votre prochaine fréquence pour reprendre la surveillance.
                        </span>
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: '#556677', marginTop: '8px', lineHeight: '1.5' }}>
                        Aucun check-in ne sera exigé jusqu'à votre date de retour. Idéal pour les voyages sans réseau.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Période de grâce */}
              <div>
                <span style={sectionLabel}>Période de grâce</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.6rem' }}>
                  {visibleGrace.map(g => (
                    <button key={g.value} onClick={() => setGracePeriod(g.value)} style={selBtn(gracePeriod === g.value)}>{g.label}</button>
                  ))}
                </div>
                <div style={{ fontSize: '12px', color: '#556677' }}>
                  Délai supplémentaire après la date prévue avant l'envoi des alertes à vos bénéficiaires
                </div>
              </div>

              {/* Message */}
              <div>
                <span style={sectionLabel}>Message pour vos bénéficiaires</span>
                <textarea value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Message personnel transmis à vos bénéficiaires si votre check-in n'est pas effectué..."
                  rows={4} style={{ ...fieldStyle, height: '100px', resize: 'vertical' }} />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                <button onClick={handleSaveSettings} disabled={loading || !isDirty} style={{
                  padding: '12px 24px', border: 'none', borderRadius: '10px',
                  fontWeight: '600', fontSize: '15px',
                  cursor: (loading || !isDirty) ? 'default' : 'pointer',
                  background: isDirty ? S : 'rgba(0,245,160,0.25)',
                  color: isDirty ? '#0A1628' : 'rgba(0,245,160,0.6)',
                  opacity: loading ? 0.6 : 1, transition: 'background 0.2s, color 0.2s',
                }}>
                  {loading ? 'Sauvegarde...' : isDirty ? 'Sauvegarder les modifications' : 'Paramètres à jour'}
                </button>
                {checkinEnabled && (
                  <button onClick={handleDisable} disabled={loading} style={{
                    padding: '12px 24px', background: 'rgba(255,71,87,0.12)',
                    color: '#FF4757', border: '1px solid rgba(255,71,87,0.3)',
                    borderRadius: '10px', fontWeight: '500', fontSize: '15px',
                    cursor: 'pointer', opacity: loading ? 0.6 : 1,
                  }}>Désactiver la protection</button>
                )}
              </div>
            </div>
          </GlowCard>
        </div>

        {/* Confirmation de présence */}
        <div style={{ marginBottom: '1.5rem' }}>
          <GlowCard>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#fff', marginBottom: '0.5rem' }}>
              Confirmer ma présence
            </h2>
            <p style={{ fontSize: '14px', color: G, marginBottom: '2rem' }}>
              {status === 'pending'
                ? "Effectuez votre premier check-in pour démarrer la surveillance et programmer la prochaine vérification."
                : "Confirmez votre présence pour reprogrammer le prochain check-in selon la fréquence choisie."}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {vacationExpired ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 20px', background: 'rgba(254,202,87,0.08)', border: '1px solid rgba(254,202,87,0.25)', borderRadius: '12px', maxWidth: '480px' }}>
                  <AlertIcon size={16} color="#FECA57" />
                  <span style={{ fontSize: '14px', color: '#FECA57', lineHeight: '1.5' }}>
                    Vos congés sont terminés. Sélectionnez une nouvelle fréquence ci-dessus et sauvegardez pour reprendre la surveillance.
                  </span>
                </div>
              ) : confirmed ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                  padding: '18px 32px', background: 'rgba(0,245,160,0.1)',
                  border: `1px solid ${S}`, borderRadius: '12px', textAlign: 'center',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '600', color: S }}>
                    <CheckIcon size={20} color={S} /> Présence confirmée !
                  </div>
                  {nextDueDate && (
                    <div style={{ fontSize: '13px', color: G }}>
                      Prochain check-in : {new Date(nextDueDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={performCheckin} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '14px 32px', background: S, color: '#0A1628',
                  border: 'none', borderRadius: '12px', fontWeight: '600', fontSize: '16px',
                  cursor: 'pointer', boxShadow: '0 0 20px rgba(0,245,160,0.3)',
                }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                >
                  <CheckIcon size={20} color="#0A1628" />
                  Je confirme ma présence
                </button>
              )}
            </div>
          </GlowCard>
        </div>

        {/* Comment ça fonctionne */}
        <GlowCard>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#fff', marginBottom: '1.5rem' }}>
            Comment ça fonctionne ?
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {[
              'Vous confirmez votre présence à la fréquence choisie',
              'Si vous ne confirmez pas dans les délais, vos bénéficiaires sont alertés',
              'Après la période de grâce, vos consignes leur sont transmises selon vos instructions',
              'Vous pouvez effectuer un check-in à tout moment pour reprogrammer la prochaine échéance',
            ].map((text, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                  background: S, color: '#0A1628',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', fontWeight: '700',
                }}>{i + 1}</div>
                <span style={{ fontSize: '14px', color: '#E8ECF1', lineHeight: '1.6', paddingTop: '4px' }}>{text}</span>
              </div>
            ))}
          </div>
        </GlowCard>

      </div>
    </div>
  );
};

export default CheckinPage;
